import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import * as apiForms from './api'
import type {
  AccessPolicyRequest,
  FieldRequest,
  FieldUpdateRequest,
  SectionRequest,
  SectionUpdateRequest,
} from './types'

export const formAdminKeys = {
  schema: (objectType: string) => ['admin', 'forms', 'schema', objectType] as const,
  policies: (objectType: string) => ['admin', 'forms', 'policies', objectType] as const,
}

export function useFormSchema(objectType: string) {
  return useQuery({
    queryKey: formAdminKeys.schema(objectType),
    queryFn: () => apiForms.getFormSchema(objectType),
  })
}

export function useAccessPolicies(objectType: string) {
  return useQuery({
    queryKey: formAdminKeys.policies(objectType),
    queryFn: () => apiForms.listAccessPolicies(objectType),
  })
}

function invalidateSchema(qc: ReturnType<typeof useQueryClient>, objectType: string) {
  qc.invalidateQueries({ queryKey: formAdminKeys.schema(objectType) })
  qc.invalidateQueries({ queryKey: ['formSchema', objectType] }) // contract reader cache
}

function invalidatePolicies(qc: ReturnType<typeof useQueryClient>, objectType: string) {
  qc.invalidateQueries({ queryKey: formAdminKeys.policies(objectType) })
  qc.invalidateQueries({ queryKey: ['contracts', 'detail'] }) // visibility may have changed
}

export function useCreateSection(objectType: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: SectionRequest) => apiForms.createSection(objectType, payload),
    onSuccess: () => invalidateSchema(qc, objectType),
  })
}

export function useUpdateSection(objectType: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { code: string; payload: SectionUpdateRequest }) =>
      apiForms.updateSection(objectType, vars.code, vars.payload),
    onSuccess: () => invalidateSchema(qc, objectType),
  })
}

export function useDeleteSection(objectType: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (code: string) => apiForms.deleteSection(objectType, code),
    onSuccess: () => invalidateSchema(qc, objectType),
  })
}

export function useCreateField(objectType: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: FieldRequest) => apiForms.createField(objectType, payload),
    onSuccess: () => invalidateSchema(qc, objectType),
  })
}

export function useUpdateField(objectType: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { fieldCode: string; payload: FieldUpdateRequest }) =>
      apiForms.updateField(objectType, vars.fieldCode, vars.payload),
    onSuccess: () => invalidateSchema(qc, objectType),
  })
}

export function useDeleteField(objectType: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (fieldCode: string) => apiForms.deleteField(objectType, fieldCode),
    onSuccess: () => invalidateSchema(qc, objectType),
  })
}

export function useUpdateSectionLayout(objectType: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { sectionCode: string; entries: apiForms.LayoutEntry[] }) =>
      apiForms.updateSectionLayout(objectType, vars.sectionCode, vars.entries),
    onSuccess: () => invalidateSchema(qc, objectType),
  })
}

export function useCreateAccessPolicy(objectType: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: AccessPolicyRequest) => apiForms.createAccessPolicy(objectType, payload),
    onSuccess: () => invalidatePolicies(qc, objectType),
  })
}

export function useUpdateAccessPolicy(objectType: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { id: number; payload: AccessPolicyRequest }) =>
      apiForms.updateAccessPolicy(objectType, vars.id, vars.payload),
    onSuccess: () => invalidatePolicies(qc, objectType),
  })
}

export function useDeleteAccessPolicy(objectType: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => apiForms.deleteAccessPolicy(objectType, id),
    onSuccess: () => invalidatePolicies(qc, objectType),
  })
}
